import axios from "axios";
import { Form, Formik } from "formik";
import { Fragment, useContext, useState } from "react";

import { ModalContext } from "app/app";
import { FieldInput } from "components/fieldInput";
import { ViewContext } from "components/lib";

import { account_sources, stateOptions } from "data";
import { validationSchema } from "./validationSchema";

import Loader from "./images/loader.gif";
import styles from "./styles.module.scss";

export function AccountModal() {
  const context = useContext(ViewContext);
  const { modal, setModal } = useContext(ModalContext);
  const [loading, setLoading] = useState(false);

  const handleFormSubmit = async (values) => {
    setLoading(true);
    try {
      const res = await axios.get(
        `/api/user/search?page=1&search=${values.owner_email}`,
      );

      if (res.data.data.data.length === 0) {
        const response = await axios.post("/api/invite", {
          email: values.owner_email,
          agency_name: values.agency_name,
          dba_name: values.dba_name ? values.dba_name.trim() : "",
          agency_npn: values.agency_npn,
          address1: values.address1,
          address2: values.address2,
          city: values.city,
          state: values.state,
          zip: values.zip,
          owner_name: `${values.owner_firstName} ${values.owner_lastName}`,
          owner_phone: values.owner_phone || "",
          type: "agency",
          source: values.source,
          certificate_contacts: {
            name: `${values.owner_firstName} ${values.owner_lastName}`,
            email: values.owner_email,
            phone: values.owner_phone || "",
          },
        });

        context.notification.show(
          "Invite sent!",
          "success",
          false,
          "toast",
          "check-circle",
        );

        if (response.data?.data?.id && modal.action) {
          modal.action({
            ...response.data.data,
            id: response.data.data.id,
            agency_name: values.agency_name,
            dba_name: values.dba_name ? values.dba_name.trim() : "",
            name: values.agency_name,
          });
        }
      } else {
        context.notification.show(
          "That email is already registered",
          "error",
          true,
          "toast",
          "x-circle",
        );
      }
    } catch (error) {
      console.error("Error:", error.response?.data || error);
      context.notification.show(
        error.response?.data?.message || "An error occurred",
        "error",
        true,
        "toast",
        "x-circle",
      );
    } finally {
      setLoading(false);
      setModal("");
    }
  };

  return (
    <Fragment>
      {modal.name === "account_modal" && (
        <div className={styles.background}>
          <div className={styles.wrapper}>
            <div className={styles.insideWrapper}>
              <div className={styles.title}>Create agency</div>
              <div className={styles.line} />
              <Formik
                validationSchema={validationSchema}
                initialValues={{
                  agency_name: "",
                  dba_name: "",
                  agency_npn: "",
                  address1: "",
                  address2: "",
                  city: "",
                  state: "",
                  zip: "",
                  owner_firstName: "",
                  owner_lastName: "",
                  owner_email: "",
                  source: "",
                }}
                onSubmit={handleFormSubmit}
              >
                {({
                  errors,
                  touched,
                  handleSubmit,
                  handleChange,
                  handleBlur,
                  submitForm,
                }) => (
                  <Fragment>
                    <Form className={styles.form} onSubmit={handleSubmit}>
                      <div
                        className={styles.fieldRow}
                        style={{ paddingBottom: 0 }}
                      >
                        <FieldInput
                          id="agency_name"
                          type="text"
                          label="Agency Name"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          required
                          showErrors
                          errors={errors.agency_name}
                          touched={touched.agency_name}
                        />
                        <FieldInput
                          id="dba_name"
                          type="text"
                          label="DBA Name"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          showErrors
                          errors={errors.dba_name}
                          touched={touched.dba_name}
                        />
                      </div>
                      <div className={styles.fieldRow}>
                        <FieldInput
                          id="agency_npn"
                          type="text"
                          label="Agency NPN"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          required
                          showErrors
                          errors={errors.agency_npn}
                          touched={touched.agency_npn}
                        />
                      </div>
                      <div className={styles.fieldRow}>
                        <FieldInput
                          id="address1"
                          type="text"
                          label="Address 1"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          required
                          showErrors
                          errors={errors.address1}
                          touched={touched.address1}
                        />
                      </div>
                      <div className={styles.fieldRow}>
                        <FieldInput
                          id="address2"
                          type="text"
                          label="Address 2"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          showErrors
                          errors={errors.address2}
                          touched={touched.address2}
                        />
                        <FieldInput
                          id="city"
                          type="text"
                          label="City"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          required
                          showErrors
                          errors={errors.city}
                          touched={touched.city}
                        />
                      </div>
                      <div className={styles.fieldRow}>
                        <FieldInput
                          id="state"
                          type="select"
                          label="State"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          required
                          showErrors
                          errors={errors.state}
                          touched={touched.state}
                          options={stateOptions}
                        />
                        <FieldInput
                          id="zip"
                          type="text"
                          label="Zip"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          required
                          showErrors
                          errors={errors.zip}
                          touched={touched.zip}
                        />
                      </div>
                      <div
                        className={styles.fieldRow}
                        style={{ paddingBottom: 0 }}
                      >
                        <FieldInput
                          id="owner_firstName"
                          type="text"
                          label="Owner First name"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          required
                          showErrors
                          errors={errors.owner_firstName}
                          touched={touched.owner_firstName}
                        />
                        <FieldInput
                          id="owner_lastName"
                          type="text"
                          label="Owner Last Name"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          required
                          showErrors
                          errors={errors.owner_lastName}
                          touched={touched.owner_lastName}
                        />
                      </div>
                      <div className={styles.fieldRow}>
                        <FieldInput
                          id="owner_email"
                          type="text"
                          label="Owner Email"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          required
                          showErrors
                          errors={errors.owner_email}
                          touched={touched.owner_email}
                        />
                        <FieldInput
                          id="source"
                          type="select"
                          label="Source"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          required
                          showErrors
                          errors={errors.source}
                          touched={touched.source}
                          options={account_sources}
                        />
                      </div>
                    </Form>
                    <div
                      className={styles.footer}
                      style={{ justifyContent: "flex-end" }}
                    >
                      <div className={styles.buttons}>
                        <button
                          className={styles.cancel}
                          onClick={() => setModal("")}
                        >
                          Cancel
                        </button>
                        <button className={styles.save} onClick={submitForm}>
                          {loading ? (
                            <img
                              src={Loader}
                              alt="Loader"
                              className={styles.loader}
                            />
                          ) : (
                            "Create"
                          )}
                        </button>
                      </div>
                    </div>
                  </Fragment>
                )}
              </Formik>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
}
